import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getAiScoringApi } from './getAiScoring.api';
import { useState } from 'react';
import { useShowToast } from '@/hooks';
import { AiScoringConstants } from '../../aiScoringConstants';

const { ICP_MAX_CHARACTER_LIMIT, errorMap, STEP_ICP, STEP_ATTRIBUTES } =
  AiScoringConstants;

export const useGetAiScoringMutations = ({
  jobData,
  setActiveStep,
}: {
  jobData: { id: string };
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const [isPolling, setIsPolling] = useState(false);
  const [isIcpGenerating, setIsIcpGenerating] = useState(false);
  const [generatedAttributes, setGeneratedAttributes] = useState<
    any[] | undefined
  >(undefined);
  const [generatedIcp, setGeneratedIcp] = useState<string | undefined>(
    undefined
  );
  const [generatedCategories, setGeneratedCategories] = useState<
    string[] | undefined
  >(undefined);
  const [hasAttemptedGeneration, setHasAttemptedGeneration] = useState(false);
  const queryClient = useQueryClient();
  const toast = useShowToast();

  // Debug wrapper for setIsPolling
  const setIsPollingWithDebug = (value: boolean, _source: string) => {
    setIsPolling(value);
  };

  // 1.1 generate icp from job jd or user input
  const generateIcpIdMutation = useMutation({
    mutationFn: (jobDescription: string) =>
      getAiScoringApi.generateIcpId(jobData?.id, {
        ai_scoring: {
          job_description: jobDescription,
        },
      }),
    onMutate: () => {
      setIsIcpGenerating(true);
      setIsPollingWithDebug(true, 'generateIcpIdMutation');
    },
    onSuccess: (response) => {
      const { data, errors } = response.data;
      const { status, result_key } = data;
      if (status === 'pending') {
        fetchIcpStatusMutation.mutate(result_key);
      } else {
        setIsPollingWithDebug(false, 'generateIcpIdMutation');
        setIsIcpGenerating(false);
      }
      if (errors.length > 0) {
        toast({
          title: 'ICP Generation Error',
          description:
            'An error occurred while generating ICP. Please try again.',
          status: 'error',
        });
      }
    },
    onError: (_error) => {
      setIsPollingWithDebug(false, 'generateIcpIdMutation');
      setIsIcpGenerating(false);
      toast({
        title: 'ICP Generation Failed',
        description: 'Please try again.',
        status: 'error',
      });
    },
  });

  // 1.2.poll for icp status, this will give us both icp and attributes
  const fetchIcpStatusMutation = useMutation({
    mutationFn: (icpId: string) =>
      getAiScoringApi.fetchIcpStatus(jobData?.id, icpId),
    onMutate: () => {
      setIsPollingWithDebug(true, 'fetchIcpStatusMutation');
      setIsIcpGenerating(true);
    },
    onSuccess: (response) => {
      const { data, errors } = response.data;
      const { status, result_key } = data;
      if (status === 'success') {
        // Check if the ICP response indicates an invalid job description
        const isInvalidJobDescription =
          data.icp === 'Invalid job description provided';

        if (isInvalidJobDescription) {
          // Skip setting ICP data for invalid job descriptions
          toast({
            title:
              'Job description not sufficient to generate ICP. Enter manually.',
            status: 'error',
          });
        } else {
          // Cap the generated ICP to the maximum character limit
          const cappedIcp =
            data.icp?.length > ICP_MAX_CHARACTER_LIMIT
              ? data.icp.substring(0, ICP_MAX_CHARACTER_LIMIT)
              : data.icp;

          setGeneratedIcp(cappedIcp);
          setGeneratedCategories(data.categories ?? []);
        }

        // Always clear loading states regardless of whether ICP was valid
        setIsPollingWithDebug(false, 'fetchIcpStatusMutation');
        setIsIcpGenerating(false);
      } else if (status === 'pending') {
        setTimeout(() => {
          fetchIcpStatusMutation.mutate(result_key);
        }, 1000);
      } else if (status === 'error') {
        setIsPollingWithDebug(false, 'fetchIcpStatusMutation');
        setIsIcpGenerating(false);
        // const errorMessage =
        //   errorMap[error] ??
        //   // error ??
        //   'Failed to generate ICP. Please try again.';
        toast({
          title: 'ICP Generation Failed',
          // description: errorMessage,
          status: 'error',
        });
      } else {
        setIsPollingWithDebug(false, 'fetchIcpStatusMutation');
        setIsIcpGenerating(false);
      }
      if (errors.length > 0) {
        toast({
          title: 'ICP Generation Error',
          status: 'error',
        });
      }
    },
    onError: (_error) => {
      setIsPollingWithDebug(false, 'fetchIcpStatusMutation');
      setIsIcpGenerating(false);
      toast({
        title: 'ICP Generation Failed',
        status: 'error',
      });
    },
  });

  // 2.1 save icp
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const saveIcpMutation = useMutation({
    mutationFn: async ({
      icp,
      categories,
    }: {
      icp: string;
      categories: string[];
    }) => {
      setIsPollingWithDebug(true, 'saveIcpMutation');
      const res = await getAiScoringApi.saveIcp(jobData?.id, {
        ai_scoring: { icp, categories },
      });
      return res;
    },
    onSuccess: (response, variables) => {
      if (!response) return;
      const { data, status, errors } = response;
      if (status === 200) {
        setGeneratedIcp(variables.icp);
        setGeneratedCategories(variables.categories ?? []);

        // Update query cache with ICP
        queryClient.setQueryData(
          ['fetchAiScoring', jobData?.id],
          (old: any) => {
            if (!old) return old;
            return {
              ...old,
              data: {
                ...old.data,
                ai_scoring: {
                  ...old.data?.ai_scoring,
                  icp: variables.icp,
                  categories: variables.categories,
                },
              },
            };
          }
        );

        // Force query refetch to ensure aiScoringData gets the latest saved ICP
        queryClient.invalidateQueries({
          queryKey: ['fetchAiScoring', jobData?.id],
        });

        // Manually trigger attribute generation
        generateAttributesMutation.mutate(variables.icp);
        // Don't set isPolling to false here - let generateAttributesMutation and its polling handle it
      } else {
        // Only clear polling state if we're not proceeding to attribute generation
        setIsPollingWithDebug(false, 'saveIcpMutation');
        // Clear generated attributes when ICP save is not successful to prevent showing old attributes
        setGeneratedAttributes(undefined);
      }

      if (errors?.length > 0 || data?.data?.error) {
        const error = errors[0] ?? data?.data?.error;
        const errorMessage = errorMap[error] ?? error;
        // Clear generated attributes on error to prevent showing old attributes
        setGeneratedAttributes(undefined);
        toast({
          title: errorMessage,
          status: 'error',
        });
      }

      return response;
    },
    onError: (_error) => {
      setIsPollingWithDebug(false, 'saveIcpMutation');
      // Clear generated attributes when ICP save fails to prevent showing old attributes
      setGeneratedAttributes(undefined);
      // Reset generation flag since we didn't even attempt attribute generation
      setHasAttemptedGeneration(false);
    },
    onSettled: (response) => {
      // Only clear isPolling if the ICP save was not successful (didn't trigger attribute generation)
      // If successful, the attribute generation process will manage the polling state
      if (!response || response.status !== 200) {
        setIsPollingWithDebug(false, 'saveIcpMutation');
      }
    },
  });

  // 2.2 generate attributes from icp when modified or updated from the user
  const generateAttributesMutation = useMutation({
    mutationFn: (icp: string) => {
      // Validate ICP length before generating attributes
      if (!icp || icp.trim().length === 0) {
        throw new Error('ICP cannot be empty');
      }

      if (icp.length > ICP_MAX_CHARACTER_LIMIT) {
        throw new Error(
          `ICP must be ${ICP_MAX_CHARACTER_LIMIT} characters or less`
        );
      }

      return getAiScoringApi.generateAttributes(jobData?.id, {
        ai_scoring: {
          icp,
        },
      });
    },
    onMutate: () => {
      setIsPollingWithDebug(true, 'generateAttributesMutation');
      // Clear old generated attributes when starting new generation to prevent showing stale data
      setGeneratedAttributes(undefined);
      // Mark that we've attempted generation so we don't fall back to saved attributes on failure
      setHasAttemptedGeneration(true);
    },
    onSuccess: (response) => {
      const { data, errors } = response.data;
      const { status, result_key, error } = data;
      if (status === 'pending') {
        setTimeout(() => {
          fetchAttributesStatusMutation.mutate(result_key);
        }, 1000);
      } else if (status === 'success') {
        setIsPollingWithDebug(false, 'generateAttributesMutation');
      } else if (status === 'error') {
        setIsPollingWithDebug(false, 'generateAttributesMutation');
        // Clear generated attributes on error to prevent incorrect step navigation
        setGeneratedAttributes(undefined);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const errorMessage =
          errorMap[error] ??
          error ??
          'Failed to generate attributes. Please try again.';
        toast({
          title: 'Attribute Generation Failed',
          // description: errorMessage,
          status: 'error',
        });
        setActiveStep((prev) => (prev === STEP_ICP ? STEP_ATTRIBUTES : prev));
        setGeneratedAttributes(undefined);
      } else {
        setIsPollingWithDebug(false, 'generateAttributesMutation');
        // Clear generated attributes on unknown status
        setGeneratedAttributes(undefined);
      }
      if (errors.length > 0) {
        toast({
          title: 'Attribute Generation Error',
          description:
            'An error occurred while generating attributes. Please try again.',
          status: 'error',
        });
      }
    },
    onError: (_error) => {
      setIsPollingWithDebug(false, 'generateAttributesMutation');
      // Clear generated attributes on error to prevent incorrect step navigation
      setGeneratedAttributes(undefined);
      toast({
        title: 'Attribute Generation Failed',
        status: 'error',
      });
      setActiveStep((prev) => (prev === STEP_ICP ? STEP_ATTRIBUTES : prev));
      setGeneratedAttributes(undefined);
    },
  });

  // 2.2 poll for attributes status
  const fetchAttributesStatusMutation = useMutation({
    mutationFn: (resultKey: string) =>
      getAiScoringApi.fetchGenerateAttributesStatus(jobData?.id, resultKey),
    onMutate: () => {
      setIsPollingWithDebug(true, 'fetchAttributesStatusMutation');
    },
    onSuccess: (response) => {
      const { data, errors } = response.data;
      const { status, result_key, attributes, error } = data;
      if (status === 'success') {
        setIsPollingWithDebug(false, 'fetchAttributesStatusMutation');

        // Store generated attributes in local state instead of query cache
        setGeneratedAttributes(attributes);
        // Reset the attempted generation flag since we got successful results
        setHasAttemptedGeneration(false);

        // on success, if we are in step 1 move to step 2
        setActiveStep((prev) => (prev === STEP_ICP ? STEP_ATTRIBUTES : prev));

        // Update ICP if present in response
        if (typeof (data as any)?.icp === 'string') {
          setGeneratedIcp((data as any).icp);
          setGeneratedCategories((data as any).categories ?? []);
        }
      } else if (status === 'pending') {
        setTimeout(() => {
          fetchAttributesStatusMutation.mutate(result_key);
        }, 1000);
      } else if (status === 'error') {
        setIsPollingWithDebug(false, 'fetchAttributesStatusMutation');
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const errorMessage =
          errorMap[error] ??
          error ??
          'Failed to generate attributes. Please try again.';
        toast({
          title: 'Attribute Generation Failed',
          // description: errorMessage,
          status: 'error',
        });
        setActiveStep((prev) => (prev === STEP_ICP ? STEP_ATTRIBUTES : prev));
        setGeneratedAttributes(undefined);
      } else {
        setIsPollingWithDebug(false, 'fetchAttributesStatusMutation');
        // Clear generated attributes on unknown status to prevent showing old attributes
        setGeneratedAttributes(undefined);
      }
      if (errors.length > 0) {
        // Clear generated attributes on error to prevent showing old attributes
        setGeneratedAttributes(undefined);
        toast({
          title: 'Attribute Generation Error',
          description:
            'An error occurred while generating attributes. Please try again.',
          status: 'error',
        });
      }
    },
    onError: (_error) => {
      setIsPollingWithDebug(false, 'fetchAttributesStatusMutation');
      toast({
        title: 'Attribute Generation Failed',
        status: 'error',
      });
      setActiveStep((prev) => (prev === STEP_ICP ? STEP_ATTRIBUTES : prev));
      setGeneratedAttributes(undefined);
    },
  });

  const getIcpCategoriesMutation = useMutation({
    mutationFn: async ({ jobId, icp }: { jobId: string; icp: string }) => {
      const res = await getAiScoringApi.getIcpCategories(jobId, icp);
      return res;
    },

    onSuccess: (_response) => {},

    onError: (_error) => {},
  });

  const isIcpLoading =
    generateIcpIdMutation.isPending ||
    fetchIcpStatusMutation.isPending ||
    isPolling;

  const isAttributesLoading =
    generateAttributesMutation.isPending ||
    fetchAttributesStatusMutation.isPending ||
    isPolling;

  return {
    generateIcpIdMutation,
    generateAttributesMutation,
    // saveIcpMutation, // Commented out - can be uncommented in future
    icpData: fetchIcpStatusMutation.data?.data?.data,
    attributesData: fetchAttributesStatusMutation.data?.data?.data,
    isLoading: isIcpLoading || isAttributesLoading,
    isIcpLoading,
    isAttributesLoading,
    isIcpGenerating,
    getIcpCategoriesMutation,
    // Manual data flow
    generatedAttributes,
    generatedIcp,
    generatedCategories,
    hasAttemptedGeneration,
    // updateGeneratedAttributes: setGeneratedAttributes,
  };
};
